/**
 * Leviathan - Terminals List Page
 * Quasar Prime LLC
 *
 * List of fuel terminals for purchasing
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import {
  Plus,
  Loader2,
  Search,
  Warehouse,
  MapPin,
  Building2,
  Fuel,
} from 'lucide-react';
import { API_URL } from '@/config/api';

interface Terminal {
  id: string;
  terminalCode: string;
  name: string;
  displayName: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  tcn: string | null;
  operator: string | null;
  isActive: boolean;
  supplier: {
    id: string;
    name: string;
    supplierNumber: string;
  } | null;
}

export default function TerminalsList() {
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const [search, setSearch] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['terminals', search, stateFilter, showInactive],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (stateFilter) params.append('state', stateFilter);
      if (!showInactive) params.append('isActive', 'true');
      params.append('limit', '100');

      const res = await fetch(`${API_URL}/api/terminals?${params}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to fetch terminals');
      return res.json();
    },
  });

  const terminals: Terminal[] = data?.items || [];
  const total = data?.total || 0;

  // Get unique states from terminals
  const states = [...new Set(terminals.map(t => t.state).filter((s): s is string => Boolean(s)))].sort();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">Terminals</h1>
          <p className="text-slate-400 mt-1">
            {total} terminals - Fuel pickup locations
          </p>
        </div>
        <Link
          to="/terminals/new"
          className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-slate-900 rounded-lg font-medium transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Terminal
        </Link>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="relative flex-1 min-w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search terminals..."
            className="w-full pl-10 pr-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
          />
        </div>

        <select
          value={stateFilter}
          onChange={(e) => setStateFilter(e.target.value)}
          className="px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
        >
          <option value="">All States</option>
          {states.map(state => (
            <option key={state} value={state}>{state}</option>
          ))}
        </select>

        <label className="flex items-center gap-2 text-slate-400 cursor-pointer">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="rounded border-slate-600 bg-slate-800 text-amber-500 focus:ring-amber-500/50"
          />
          <span className="text-sm">Show Inactive</span>
        </label>
      </div>

      {/* Terminals Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
        </div>
      ) : terminals.length === 0 ? (
        <div className="text-center py-12 bg-slate-800/50 rounded-xl border border-slate-700/50">
          <Warehouse className="w-12 h-12 text-slate-600 mx-auto mb-4" />
          <p className="text-slate-400 mb-4">No terminals found</p>
          <Link
            to="/terminals/new"
            className="text-amber-400 hover:text-amber-300"
          >
            Add your first terminal
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {terminals.map((terminal) => (
            <TerminalCard
              key={terminal.id}
              terminal={terminal}
              onClick={() => navigate(`/terminals/${terminal.id}`)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function TerminalCard({ terminal, onClick }: { terminal: Terminal; onClick: () => void }) {
  return (
    <div
      onClick={onClick}
      className={`relative bg-slate-800/50 border rounded-xl p-5 cursor-pointer transition-all hover:border-amber-500/50 hover:bg-slate-800/70 ${
        terminal.isActive ? 'border-slate-700/50' : 'border-slate-700/30 opacity-60'
      }`}
    >
      {/* Corner accents */}
      <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber-500/50 rounded-tl-xl" />
      <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber-500/50 rounded-tr-xl" />
      <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber-500/50 rounded-bl-xl" />
      <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber-500/50 rounded-br-xl" />

      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <Warehouse className="w-5 h-5 text-amber-400" />
          <div>
            <h3 className="font-semibold text-slate-100">{terminal.name}</h3>
            <span className="text-xs text-slate-500 font-mono">{terminal.terminalCode}</span>
          </div>
        </div>
        {!terminal.isActive && (
          <span className="px-2 py-0.5 text-xs bg-slate-700 text-slate-400 rounded">
            Inactive
          </span>
        )}
      </div>

      {terminal.tcn && (
        <div className="mb-3">
          <span className="text-amber-400 text-sm font-mono">{terminal.tcn}</span>
        </div>
      )}

      <div className="flex items-center gap-2 text-slate-400 text-sm mb-2">
        <MapPin className="w-4 h-4" />
        <span>
          {terminal.city && terminal.state
            ? `${terminal.city}, ${terminal.state}`
            : terminal.state || 'Location not set'}
        </span>
      </div>

      {terminal.operator && (
        <div className="flex items-center gap-2 text-slate-500 text-xs">
          <Building2 className="w-3 h-3" />
          <span>Operated by: {terminal.operator}</span>
        </div>
      )}

      {terminal.supplier && (
        <div className="flex items-center gap-2 text-slate-500 text-xs mt-1">
          <Fuel className="w-3 h-3" />
          <span>Supplier: {terminal.supplier.name}</span>
        </div>
      )}
    </div>
  );
}
